import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { Provision } from '../../objects/provision';
import { ProvisionAnalysisRequestItem } from '../../objects/provision-analysis';
import { ProvisionsService } from '../entity-services/provisions.service';

/** Sentinel value (from the contract-types catalog) meaning "applies to every contract type". */
export const ALL_CONTRACT_TYPES = 'All Contract Types';

/** Approved provision = officially approved (skips risk scoring) AND its action is done. */
export function isApprovedProvision(p: Provision): boolean {
  return p.approvedProvision === true && p.actionCompleted === true;
}

/**
 * Does this provision apply to `contractType`? True when the provision carries the
 * "All Contract Types" sentinel, or names this type exactly (case-insensitive).
 *
 * Matching is exact-after-trim on purpose. Callers pass either a saved
 * `contract.contractType` (chosen from the catalog, so it matches) or the intake AI's
 * free-text `agreementType` (which may not). A fuzzy match would quietly pull in
 * provisions the customer never scoped to this document — the failure mode is silent
 * and looks authoritative, so a miss that shows fewer provisions is the safer answer.
 */
export function appliesToContractType(p: Provision, contractType: string | null | undefined): boolean {
  const types = p.contractTypes || [];
  if (types.includes(ALL_CONTRACT_TYPES)) return true;
  const wanted = (contractType || '').trim().toLowerCase();
  if (!wanted) return false;
  return types.some(t => (t || '').trim().toLowerCase() === wanted);
}

/**
 * The approved-provision set that a document is reviewed against.
 *
 * This rule used to live private inside AIAnalysisDialogComponent, which was fine while
 * the Analysis tab was its only consumer. The workflow request's Document Review is a
 * second consumer, and two copies of "what counts as an approved provision" would drift
 * silently — the review would disagree with the Playbook about which provisions apply and
 * nothing would error. One home, both callers.
 */
@Injectable({ providedIn: 'root' })
export class ApprovedProvisionsService {
  private provisionsService = inject(ProvisionsService);

  /**
   * The approved provisions applicable to `contractType`, emitted once.
   * Warms the provisions cache if it hasn't loaded yet (a dialog can open before the
   * app-wide fetch settles).
   */
  applicableFor(contractType: string | null | undefined): Observable<Provision[]> {
    return this.provisionsService.allProvisions$.pipe(
      take(1),
      switchMap(provisions =>
        provisions && provisions.length > 0
          ? of(provisions)
          : this.provisionsService.refreshAllProvisionsRecords().pipe(take(1), map(r => r || []))
      ),
      map(provisions =>
        (provisions || []).filter(p => isApprovedProvision(p) && appliesToContractType(p, contractType))
      )
    );
  }

  /**
   * The payload the provision-analysis backend expects.
   *
   * `recommendedLanguage` comes from `description` — there is no `recommendedLanguage`
   * field on a Provision. Don't "correct" this.
   */
  toRequestItems(provisions: Provision[]): ProvisionAnalysisRequestItem[] {
    return (provisions || []).map(p => ({
      provNum: p.provNum || '',
      provName: p.provName || 'Unnamed Provision',
      recommendedLanguage: p.description || ''
    }));
  }
}
